import { css } from "@compiled/react";
import { FilterDefinition } from "src/uniform/contenttypes/filterDefinition";

import { token } from "@atlaskit/tokens";

import { Button } from "@/libraries/design/components/button/src/button";
import { wacToken } from "@/libraries/design/foundations";
import { Icon } from "@/libraries/wpl/foundations/src/icon";

import type { SubFilterObject, UniformFilterObject } from "../utils/types";

/**
 * Marker attribute placed on every element that should count as "inside" a
 * filter control for outside-click detection. The parent uses this to decide
 * whether to commit + close open dropdowns.
 */
export const FILTER_DROPDOWN_DATA_ATTR = "data-filter-dropdown";

export interface FilterControlProps {
    readonly filter: FilterDefinition;
    readonly activeCount: number;
    readonly isOpen: boolean;
    readonly subFilter: UniformFilterObject[string] | undefined;
    readonly labelMap: Record<string, string> | undefined;
    readonly onButtonClick: () => void;
    readonly onOptionToggle: (optionId: string) => void;
}

const sanitizeForId = (value: string) => value.replace(/[^\w-]/g, "-");

const buildPanelId = (filterId: string) =>
    `filter-panel-${sanitizeForId(filterId)}`;

/**
 * One filter's full UI: a clickable pill-style trigger button and the
 * floating options panel that appears beneath it when open.
 */
export const FilterControl = ({
    filter,
    activeCount,
    isOpen,
    subFilter,
    labelMap,
    onButtonClick,
    onOptionToggle
}: FilterControlProps) => {
    const filterLabel = filter.label ?? filter.id;
    const panelId = buildPanelId(filter.id);
    // Fold the count into the accessible name; the visible badge is aria-hidden.
    const accessibleName =
        activeCount > 0
            ? `${filterLabel}, ${activeCount} selected`
            : filterLabel;

    return (
        <div>
            <Button
                variant="secondary"
                size="small"
                className="body-md"
                css={[
                    triggerButtonStyle,
                    activeCount && triggerButtonActiveStyle
                ]}
                aria-label={accessibleName}
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                    event.preventDefault();
                    onButtonClick();
                }}
                dataEventProps={{
                    "data-product-key": "uniform",
                    "data-event": "clicked",
                    "data-event-component": "button",
                    "data-event-container": "searchWithFilters",
                    "data-label": filterLabel
                }}
            >
                {filterLabel} <DropdownArrow isOpen={isOpen} />
                <span
                    className="fine-print"
                    css={activeCountBadgeStyle}
                    aria-hidden="true"
                >
                    {activeCount || ""}
                </span>
            </Button>
            {isOpen && subFilter && (
                <OptionsPanel
                    panelId={panelId}
                    groupLabel={filterLabel}
                    filterId={filter.id}
                    options={subFilter}
                    onToggle={onOptionToggle}
                    labelMap={labelMap}
                />
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Internal sub-components (private to this file)
// ---------------------------------------------------------------------------

const DropdownArrow = ({ isOpen }: { readonly isOpen: boolean }) => (
    <svg
        width="10"
        height="6"
        viewBox="0 0 10 6"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        css={dropdownArrowStyle}
        style={{ transform: isOpen ? "rotate(180deg)" : undefined }}
        aria-hidden="true"
        focusable="false"
    >
        <path
            d="M9 1.18164L5 4.818L1 1.18164"
            stroke={wacToken("color.primary.black")}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

interface OptionsPanelProps {
    readonly panelId: string;
    readonly groupLabel: string;
    readonly filterId: string;
    readonly options: SubFilterObject;
    readonly onToggle: (optionId: string) => void;
    /** Optional map of option value → display label. Falls back to the value itself. */
    readonly labelMap: Record<string, string> | undefined;
}

const OptionsPanel = ({
    panelId,
    groupLabel,
    filterId,
    options,
    onToggle,
    labelMap
}: OptionsPanelProps) => (
    <div
        id={panelId}
        role="group"
        aria-label={groupLabel}
        css={panelStyle}
        className={filterId}
        {...{ [FILTER_DROPDOWN_DATA_ATTR]: "" }}
    >
        <div css={optionListStyle}>
            {Object.keys(options).map((optionId, index) => (
                <OptionCheckbox
                    key={optionId}
                    optionId={optionId}
                    label={labelMap?.[optionId] ?? optionId}
                    checked={options[optionId]?.stagedActive ?? false}
                    inputId={buildOptionInputId(filterId, index)}
                    onToggle={onToggle}
                />
            ))}
        </div>
    </div>
);

const buildOptionInputId = (filterId: string, index: number) =>
    `option-${sanitizeForId(filterId)}-${index}`;

interface OptionCheckboxProps {
    readonly optionId: string;
    readonly label: string;
    readonly checked: boolean;
    readonly inputId: string;
    readonly onToggle: (optionId: string) => void;
}

const OptionCheckbox = ({
    optionId,
    label,
    checked,
    inputId,
    onToggle
}: OptionCheckboxProps) => (
    <label htmlFor={inputId} css={optionLabelStyle}>
        <input
            id={inputId}
            type="checkbox"
            checked={checked}
            onChange={() => onToggle(optionId)}
            css={hiddenCheckboxStyle}
        />
        <span css={checkboxStyle} className={checked ? "checked" : ""}>
            <span css={[checkmarkStyle, checked && checkmarkVisibleStyle]}>
                <Icon
                    name="featurecheckmark"
                    size="xs"
                    color={wacToken("color.neutral.light0")}
                />
            </span>
        </span>
        {label}
    </label>
);

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const triggerButtonStyle = css({
    display: "flex",
    alignItems: "center",
    gap: token("space.050")
});

const triggerButtonActiveStyle = css({
    borderColor: wacToken("color.primary.blue"),
    color: wacToken("color.primary.blue")
});

const activeCountBadgeStyle = css({
    "display": "inline-flex",
    "alignItems": "center",
    "justifyContent": "center",
    "minWidth": "18px",
    "height": "18px",
    "borderRadius": "50%",
    "backgroundColor": wacToken("color.primary.blue"),
    "color": wacToken("color.neutral.light0"),
    "fontSize": "11px",
    "lineHeight": 1,
    "padding": "0 4px",
    "visibility": "hidden",
    "&:not(:empty)": {
        visibility: "visible"
    }
});

const dropdownArrowStyle = css({
    transition: "transform 0.2s ease"
});

const panelStyle = css({
    position: "absolute",
    backgroundColor: wacToken("color.neutral.light0"),
    borderRadius: "16px",
    zIndex: 2,
    insetBlockStart: "55px",
    padding: token("space.200"),
    boxShadow: token("elevation.shadow.overlay")
});

const optionListStyle = css({
    display: "flex",
    flexDirection: "column"
});

const optionLabelStyle = css({
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    marginBlockStart: token("space.100")
});

const checkboxStyle = css({
    "width": "20px",
    "height": "20px",
    "borderRadius": "3px",
    "border": `2px solid ${wacToken("color.neutral.dark100")}`,
    "flexBasis": "20px",
    "marginInlineEnd": token("space.100"),
    "background": wacToken("color.neutral.light0"),
    "display": "flex",
    "justifyContent": "center",
    "alignItems": "center",
    ":hover": {
        border: `2px solid ${wacToken("color.primary.blue")}`
    },
    "&.checked": {
        background: wacToken("color.primary.blue"),
        border: `2px solid ${wacToken("color.primary.blue")}`
    }
});

const checkmarkStyle = css({
    opacity: 0
});

const checkmarkVisibleStyle = css({
    opacity: 1
});

const hiddenCheckboxStyle = css({
    // Visually hidden but kept focusable; the visible sibling box shows the focus ring.
    "position": "absolute",
    "opacity": 0,
    "width": "20px",
    "height": "20px",
    "margin": 0,
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "&:focus-visible + span": {
        outline: `2px solid ${wacToken("color.primary.blue")}`,
        outlineOffset: "2px"
    }
});
